import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, merge } from 'rxjs';
import { map, mergeMap, catchError, tap, withLatestFrom, exhaustMap, concatMap, switchMap } from 'rxjs/operators';
import { RidersService } from '../services/riders-server.service';
import * as ridersActions from '../store/actions/riders.action';
import * as ridersSelectors from '../store/selectors/riders.selector';
import { Store, select } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import * as authActions from '../../auth/store/actions/auth.action';

@Injectable()
export class RidersEffects {
    
    loadAllRiders$ = createEffect(() => this.actions$.pipe(
     ofType(ridersActions.loadAllRiders),
     exhaustMap((action) =>
        this.ridersService.getRiders(action.page, action.limit, action.query)
        .pipe(
          tap(riders => console.log('Riders loaded all from server', riders)),
          map(riders => ridersActions.loadAllRidersSuccess({ riders })),
          catchError((error) => {
            if(error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            } 
            this.toastr.error('Error al cargar riders');
            return of(ridersActions.loadAllRidersFailure({ error }));
          })
        ))
     )
    );

    loadRidersLocations$ = createEffect(() => this.actions$.pipe(
      ofType(ridersActions.loadRidersLocations),
      switchMap(() =>
          this.ridersService.getRidersLocations()
          .pipe(
            tap(response => console.log('Riders locations loaded from server', response)),
            map(response => ridersActions.loadRidersLocationsSuccess({ ridersLocations: response.rider_locations })),
            catchError((error) => {
              if(error.status === 401) {
                this.toastr.error('Usuario no autenticado');
                return of(authActions.logout());
              } 
              return of(ridersActions.loadRidersLocationsFailure({ error: 'Error al cargar riders por location' }));
            })
          ))
      )
      );
      
    searchRiders$ = createEffect(() => this.actions$.pipe(
     ofType(ridersActions.searchRiders),
     switchMap((action) =>
        this.ridersService.getRiders(action.page, action.limit, action.query, action.vehicle_type)
        .pipe(
          tap(riders => console.log('Filtered riders loaded from server', riders)),
          map(riders => ridersActions.searchRidersSuccess({ riders })),
          catchError(() => EMPTY)
        ))
     )
    );

    createRider$ = createEffect(() => 
      this.actions$.pipe(
     ofType(ridersActions.createRider),
     withLatestFrom(this.store.pipe(select(ridersSelectors.selectRider))),
     switchMap(([action, rider]) =>
        this.ridersService.createRider(rider).pipe(
          tap(() => {
            this.snackBar.open(
              'Rider creado exitosamente',
              'Cerrar',
              {
                duration: 3000
              }
            );
          }),
          map((rider) => {
            this.toastr.success('Rider creado');
            this.router.navigate(['/riders']);
            return ridersActions.createRiderSuccess({ rider });
          }),
          catchError((error) =>{
            if (error.status === 500) {
              this.toastr.error('Hubo un error en los datos ingresados, verifica que el correo ingresado no exista aun.');
            }
            this.toastr.error('Problemas creando rider');
            return of(ridersActions.createRiderFailure({ error }));
          })
        ))
     )
    );

    editRider$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ridersActions.editRider),
        withLatestFrom(this.store.pipe(select(ridersSelectors.selectRider))),
        mergeMap(([action, rider]) =>
          this.ridersService.editRider(rider).pipe(
            tap(() => {
              this.snackBar.open(
                'Rider actualizado exitosamente',
                'Cerrar',
                {
                  duration: 3000
                }
              );
            }),
            map((rider) => {
              this.toastr.success('Rider actualizado');
              this.router.navigate(['/riders']);
              return ridersActions.editRiderSuccess({ rider });
            }),
            catchError((error) => {
              this.toastr.error('Problemas actualizando rider');
              return of(ridersActions.editRiderFailure({ error }));
            })
          )
        )
      )
    );

    loadRiderById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ridersActions.loadRiderById),
        switchMap((action) =>
          this.ridersService.getRiderById(action.id).pipe(
            map(rider => ridersActions.loadRiderByIdSuccess({ rider })),
            catchError((error) => {
              this.toastr.error('Problemas, vuelve a intentarlo');
              return of(ridersActions.loadRiderByIdFailure({ error }));
            })
          )
        )
      )
    );

    
  loadRiderByIdSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ridersActions.loadRiderByIdSuccess),
      tap(({ rider }) => this.router.navigate(['riders/edit', rider.id]))
    ),
    { dispatch: false }
  );

  resetRider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ridersActions.resetRider),
      tap(() => this.router.navigate(['/riders']))
    ),
    { dispatch: false }
  );

  selectRider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ridersActions.selectRider),
      mergeMap(({ rider_id }) => {
        return this.ridersService.getRiderById(rider_id).pipe(
          map(rider => ridersActions.selectRiderSuccess({ rider })),
          catchError((error) => {
            this.toastr.error('Problemas, vuelve a intentarlo');
            return of(ridersActions.loadRiderByIdFailure({ error }));
          })
        );
      })
    ));

    deleteRider$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ridersActions.deleteRider),
        switchMap((action) =>
          this.ridersService.deleteRider(action.id).pipe(
            tap(() => {
              this.snackBar.open(
                'Rider eliminado exitosamente',
                'Cerrar',
                {
                  duration: 3000
                }
              );
            }),
            map(() => {
              this.toastr.success('Rider eliminado');
              return ridersActions.deleteRiderSuccess({ id: action.id });
            }),
            catchError((error) => {
              this.toastr.error('Problemas eliminando rider');
              return of(ridersActions.deleteRiderFailure({ error }));
            })
          )
        )
      )
    );

      constructor(
        private actions$: Actions,
        private ridersService: RidersService,
        private store: Store,
        private snackBar: MatSnackBar,
        private toastr: ToastrService,
        private router: Router
      ) {}
    }